import {
  findEventEntry,
  isNumber,
  type EventCatalogPrice,
  type EventCatalogTier,
} from "@repo/vihangam-yog-shared";

import type { Registration } from "./shared";

/**
 * Which place type a paid booking holds and how many Kunds that is, for
 * the Kund tally, the guest's Details and the Reception card. Nothing here
 * reads the chapter config (guestFormat.ts does when it loads), so the
 * Reception card can use it too.
 */

/** The booking fields a Kund count reads. */
type KundBooking = Pick<
  Registration,
  "bookingType" | "paymentBreakdown" | "paidAmountCents" | "totalPeople"
>;

/** Units of the place type a booking bought: Singapore sells up to 12
 *  shared places or 3 Kunds in one booking, and older bookings bought one.
 *  The router sends tierQuantity at runtime but its output type doesn't
 *  name it yet. */
export function tierQuantityOf(
  r: Pick<Registration, "paymentBreakdown">,
): number {
  const b = r.paymentBreakdown;
  if (
    b !== null &&
    "tierQuantity" in b &&
    isNumber(b.tierQuantity) &&
    b.tierQuantity > 0
  ) {
    return b.tierQuantity;
  }
  return 1;
}

/**
 * The place type a paid booking holds, from its payment breakdown. Some
 * early card bookings were written with no breakdown, so those match what
 * they paid to the largest place type at or below it (an add-on like a
 * thali then doesn't turn a Shared Kund into a Full one).
 */
export function tierOf(
  r: Pick<Registration, "paymentBreakdown" | "paidAmountCents">,
  price: EventCatalogPrice,
): EventCatalogTier | undefined {
  const tierId = r.paymentBreakdown?.tierId;
  const named = tierId ? price.tiers.find((t) => t.id === tierId) : undefined;
  if (named) {
    return named;
  }
  const amount = r.paidAmountCents;
  if (amount === null) {
    return undefined;
  }
  return [...price.tiers]
    .sort((a, b) => b.amountCents - a.amountCents)
    .find((t) => amount >= t.amountCents);
}

/**
 * Kunds a paid booking takes on its place type: one per unit bought, or
 * two per unit when its group is big enough to split across two (the
 * tier's `twoKundsFrom`: 6 people on a Full Kund in Australia).
 */
export function kundUnitsOf(
  r: KundBooking,
  tier: EventCatalogTier | undefined,
): number {
  const units = tierQuantityOf(r);
  const splitFrom = tier?.twoKundsFrom;
  const splits =
    r.bookingType === "paid" &&
    splitFrom !== undefined &&
    r.totalPeople >= splitFrom * units;
  return splits ? units * 2 : units;
}

/**
 * The Kunds a big group splits across, from its event's catalogue, or null
 * when the booking stays on the place it bought. The guest's Details and
 * the Reception card say so.
 */
export function splitKundsOf(
  r: KundBooking & Pick<Registration, "eventSlug">,
): number | null {
  const price = findEventEntry(r.eventSlug)?.price;
  const tier = price ? tierOf(r, price) : undefined;
  const kunds = kundUnitsOf(r, tier);
  return kunds > tierQuantityOf(r) ? kunds : null;
}
